import { ColorPicker } from "common/ColorPicker";
import { Icon } from "common/Icon";
import { StandardLayout } from "common/layouts/StandardLayout";
import { CheckboxGroup } from "common/styleguide/CheckboxGroup";
import { ConfirmationDialog } from "common/styleguide/ConfirmationDialog";
import { CTA } from "common/styleguide/CTA";
import { EventsEmbed } from "features/embed/EventsEmbed";
import { OrderedMap, Set } from "immutable";
import { request } from "lib/apiv3/utils/request";
import _ from "lodash";
import queryString from "query-string";
import React from "react";
import styles from "./EventsEmbedConfigurator.scss";

const displayInfo = OrderedMap({
  showDateTime: "Date and time",
  showOrganizers: "Organizers",
  showLocation: "Location",
  showImage: "Image",
});

export class EventsEmbedConfigurator extends React.PureComponent {
  constructor(props) {
    super(props);
    const params = queryString.parse(this.props.location.search);
    this.state = {
      embedProps: {
        clubId: params.clubId,
        showDateTime: true,
        showOrganizers: true,
        showLocation: true,
        showImage: true,
      },
      showColorPicker: false,
      showEmbedCodeDialog: false,
      availableTags: [],
    };
  }

  componentDidMount = () => {
    const { clubId } = this.state.embedProps;
    clubId &&
      request.get(`/organizations/${clubId}/event_tags.json`).then(({ results }) => {
        this.setState({ availableTags: results.map((t) => t.name) });
      });
  };

  updateEmbedProp = (key, value) => {
    this.setState((state) => {
      const shouldOmit = value !== false && !value; // allow false values to be set, mainly for displayInfo
      const newEmbedProps = shouldOmit
        ? _.omit(state.embedProps, key)
        : Object.assign({}, state.embedProps, { [key]: value });
      return {
        embedProps: newEmbedProps,
      };
    });
  };

  onFieldChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;

    this.updateEmbedProp(name, value);
  };

  showColorPicker = () => {
    this.setState({ showColorPicker: true });
  };

  hideColorPicker = () => {
    this.setState({ showColorPicker: false });
  };

  colorChanged = (hex) => {
    this.updateEmbedProp("color", hex.replace("#", ""));
  };

  showEmbedCodeDialog = (e) => {
    e.preventDefault();
    this.setState({ showEmbedCodeDialog: true });
  };

  hideEmbedCodeDialog = () => {
    this.setState({ showEmbedCodeDialog: false });
  };

  getSelectedDisplayInfo = () => {
    let selected = Set();
    displayInfo.forEach((k, param) => {
      if (this.state.embedProps[param]) {
        selected = selected.add(param);
      }
    });
    return selected;
  };

  displayInfoChanged = (selected) => {
    displayInfo.forEach((k, param) => {
      this.updateEmbedProp(param, selected.has(param));
    });
  };

  render() {
    const { availableTags, showEmbedCodeDialog, embedProps, showColorPicker } = this.state;
    const { clubId } = embedProps;

    const listDisplay = embedProps.display === "list";
    const calendarDisplay = !embedProps.display || !listDisplay;

    // generate a url-encoded query string
    const keys = _.uniq(_.keys(embedProps).concat(displayInfo.keySeq().toArray()));
    const keysForQuerystring = _.filter(keys, (key) => {
      return EventsEmbed.defaultProps[key] !== embedProps[key]; // exclude all default values
    });
    const querystring = keysForQuerystring
      .map((key) => {
        const fullKey = encodeURIComponent(key);
        const escapedValue = encodeURIComponent(embedProps[key]);
        return `${fullKey}=${escapedValue}`;
      })
      .join("&");

    return (
      <StandardLayout>
        <div className={styles.EventsEmbedConfigurator}>
          <div className={styles.LeftPanel}>
            <form submit={this.onSubmit}>
              <div className={styles.section}>
                <h2>Customize Your Embed</h2>
              </div>
              <div className={styles.section}>
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  name="title"
                  placeholder="Title"
                  value={embedProps.title || ""}
                  onChange={this.onFieldChange}
                  autoComplete="off"
                />
              </div>
              <div className={styles.section}>
                <label htmlFor="description">Description</label>
                <input
                  type="text"
                  name="description"
                  placeholder="Description"
                  value={embedProps.description || ""}
                  onChange={this.onFieldChange}
                  autoComplete="off"
                />
              </div>
              <div className={styles.section}>
                <label htmlFor="tagname">Filter by tag</label>
                <select name="tagname" value={embedProps.tagname} onChange={this.onFieldChange}>
                  <option value="">- Any tag</option>
                  {availableTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.section}>
                <label>Choose a style</label>
                <CTA
                  inline
                  secondary={!listDisplay}
                  onClick={() => this.updateEmbedProp("display", "list")}
                  className={styles.listButton}
                >
                  <Icon i="list-icon" /> List
                </CTA>
                <CTA
                  inline
                  secondary={!calendarDisplay}
                  onClick={() => this.updateEmbedProp("display", "calendar")}
                  margins={false}
                >
                  <Icon i="calendar-icon" /> Calendar
                </CTA>
              </div>
              {listDisplay && (
                <div className={styles.section}>
                  <label>Number of weeks to display</label>
                  <select
                    name="listDays"
                    value={embedProps.listDays || EventsEmbed.defaultProps.listDays}
                    onChange={this.onFieldChange}
                  >
                    {[1, 2, 3, 4, 8, 12].map((w) => (
                      <option key={w} value={w * 7}>
                        {w} week{w > 1 && "s"}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className={styles.section}>
                <label>Choose a link color</label>
                <div
                  ref={(r) => {
                    this._colorSwatch = r;
                  }}
                  style={{
                    backgroundColor: "#" + (embedProps.color || EventsEmbed.defaultProps.color),
                  }}
                  className={styles.swatch}
                  onClick={this.showColorPicker}
                />
                {showColorPicker && (
                  <ColorPicker
                    color={"#" + embedProps.color}
                    defaultColor={"#" + EventsEmbed.defaultProps.color}
                    target={this._colorSwatch}
                    onChange={(c) => this.colorChanged(c.hex)}
                    onRequestClose={this.hideColorPicker}
                  />
                )}
              </div>
              <div className={styles.section}>
                <label>Display information</label>
                <CheckboxGroup
                  name="displayInfo"
                  options={displayInfo}
                  selected={this.getSelectedDisplayInfo()}
                  onChange={this.displayInfoChanged}
                />
              </div>
              <div className={styles.section}>
                <CTA onClick={this.showEmbedCodeDialog}>Embed Code</CTA>
              </div>
            </form>
          </div>
          <div className={styles.Preview}>
            <div className={styles.embed}>
              {clubId && <EventsEmbed {...embedProps} />}
              {!clubId && 'Provide "clubId" in the URL'}
            </div>
          </div>
        </div>
        {showEmbedCodeDialog && (
          <ConfirmationDialog
            title={"Embed code for "}
            confirmText="Got it!"
            onRequestClose={this.hideEmbedCodeDialog}
            onCancel={this.hideEmbedCodeDialog}
            onConfirm={this.hideEmbedCodeDialog}
          >
            <textarea
              className={styles.embedCode}
              defaultValue={`<iframe src="https://ridewithgps.com/embeds?type=events&${querystring}" style="width: 1px; min-width: 100%; height: 700px; border: none;" scrolling="no"></iframe>`}
            />
          </ConfirmationDialog>
        )}
      </StandardLayout>
    );
  }
}
